import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetAllStaffQuery } from "../../../store/appApi";
import { formatRoleLabel } from "./advancesConfig";
import { createAdvanceApi } from "../../../services/advances";

const darkPanel =
  "rounded-3xl border border-gray-200 bg-white text-gray-900 shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const EMPTY_FORM = {
  employeeId: "",
  type: "Advance",
  date: new Date(),
  amount: "",
  remarks: "",
};

const AddAdvance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const { data: staffData, isLoading: staffLoading } = useGetAllStaffQuery({
    page: 1,
    limit: 500,
  });

  const employees = Array.isArray(staffData?.staff)
    ? staffData.staff.filter((item) => item?.status !== false)
    : [];

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Enter inside a text field shouldn't silently submit the form — only the
  // Add button should trigger a save.
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && e.target.type !== "submit") {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.employeeId) {
      Swal.fire({
        icon: "warning",
        title: "Select employee",
        text: "Please choose an employee for this entry.",
      });
      return;
    }

    const numericAmount = Number(form.amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }

    try {
      setSaving(true);
      await createAdvanceApi({
        employeeId: form.employeeId,
        type: form.type,
        date: form.date.toISOString(),
        amount: numericAmount,
        remarks: form.remarks,
      });

      dispatch(appApi.util.invalidateTags(["Advances"]));

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: `${form.type} entry recorded successfully.`,
        timer: 1800,
        showConfirmButton: false,
      });

      // Fresh blank sheet for the next entry — keep the employee selected
      // since advances are usually logged in quick batches per person.
      setForm((prev) => ({
        ...EMPTY_FORM,
        employeeId: prev.employeeId,
      }));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message ||
          "Something went wrong while saving this entry.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Add Advance" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className={`${darkPanel} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
              Employee Advances
            </div>
            <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
              Add Advance / Deduction
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              Record a cash advance given to an employee, or a deduction
              against their outstanding balance. Every entry appears
              immediately on that employee's ledger.
            </p>
            <button
              type="button"
              className="mt-4 rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
              onClick={() => navigate("/advances")}
            >
              View All Advances
            </button>
          </div>
        </section>

        <form
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className={`${darkPanel} p-5 md:p-6`}
        >
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass}>Employee</label>
              <select
                className={inputClass}
                autoComplete="off"
                value={form.employeeId}
                onChange={(event) => updateField("employeeId", event.target.value)}
                disabled={staffLoading}
              >
                <option value="">
                  {staffLoading ? "Loading employees…" : "Select employee"}
                </option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.name} - {formatRoleLabel(employee.role)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Entry Type</label>
              <div className="flex gap-2">
                {["Advance", "Deduction"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateField("type", option)}
                    className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                      form.type === option
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : "border-gray-300 bg-white text-gray-600 hover:border-amber-400"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>Date</label>
              <DatePicker
                selected={form.date}
                onChange={(date) => date && updateField("date", date)}
                className={inputClass}
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </div>

            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={inputClass}
                autoComplete="off"
                value={form.amount}
                onChange={(event) => updateField("amount", event.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Remarks</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={form.remarks}
                onChange={(event) => updateField("remarks", event.target.value)}
                placeholder="Reason / note for this entry"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end border-t border-gray-200 pt-5">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-amber-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : `Add ${form.type}`}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddAdvance;
